var fitt = require('fitt'),
memSize = require('fitt-mem-size'),
http = require('fitt-http'),
parseMaxAge = http.parseMaxAge,
LRUCache = require('stale-lru-cache'),
_orderBy = require('lodash/orderBy'),
_clamp = require('lodash/clamp'),
ns = 'fitt-cache',
debug = require('debug')(ns),
now = Date.now,
PROTOTYPE = fitt.PROTO,
empty = fitt.empty,

MAX_MEM_SIZE = 134217728, // 128MB max (actual shallow size)
MAX_CACHE_SIZE = 4096, // number of items, not actual size
MAX_PURGE_ATTEMPTS = 5,
ONE_HUNDRED = 100,
MAX_PCT = 50,
MIN_PCT = 10,
MAX_PURGE_INTERVAL = 45000,
MIN_PURGE_INTERVAL = 5000,

PROP_USED = 'used',
PROP_SIZE = 'size',
PROP_MAXAGE = 'maxAge',
SORT_ASC = 'asc',
SORT_DESC = 'desc',
PROP_MEMSIZE = '__memsize';

/**
                             * In-memory caching behavior
                             *
                             * <pre>
                             Usage:
                             var cache = new Cache();
                            
                             // HTTP caching
                             cache.getHttp('https://httpbin.org/get')
                                .then((response) => {
                                    // response.fromCache will be a boolean indicating whether the response came from cache or request
                                });
                            
                             // Get/Set
                             cache.set('foo', 'bar'); // returns true
                             cache.get('foo'); // returns 'bar'
                             </pre>
                             *
                             * @class
                             * @name fitt.Cache
                             * @param {Object} config Contains configuration options for instance
                             * @public
                             */

function Cache(config) {
    var me = this,
    defaults = {
        maxSize: MAX_CACHE_SIZE,
        /*
                                  * Re-enabling, and ensuring in our request handler that we check
                                  * our meta-data on the response so we don't inadvertantly re-set the
                                  * expire time
                                 */
        staleWhileRevalidate: 300 };


    if (config) {
        config = fitt.mix({}, [defaults, config]);
    } else {
        config = defaults;
    }

    me.store = new LRUCache(config);
    me.timer =
    me.lastPurger =
    me.lastPurgeDate =
    me.currentMaxPct =
    me.currentInterval =
    me.lastPurgeAt = null;
    me.purgeAttempts =
    me.lastPurgeItemCount =
    me.totalSuccessfulPurges =
    me.totalPurgeIterations =
    me.lastPurgeMemFreed = 0;
    me.maxMemSize = config.maxMemSize || MAX_MEM_SIZE;
    me.maxPurgeAttemps = config.maxPurgeAttemps || MAX_PURGE_ATTEMPTS;
    me.maxPurgeInterval = config.maxPurgeInterval || MAX_PURGE_INTERVAL;
    me.minPurgeInterval = config.minPurgeInterval || MIN_PURGE_INTERVAL;
    me.minPurgePct = config.minPurgePct || MIN_PCT;
    me.maxPurgePct = config.maxPurgePct || MAX_PCT;
}

/**
   * Removes an item from the cache
   *
   * @name fitt.Cache#delete
   * @function
   * @public
   * @param key
   * @returns {Boolean} Indicates whether or not the operation was successful
   */

function deleteItem(key) {
    return this.store.delete(key);
}

/**
   * Get an item from the cache
   *
   * @name fitt.Cache#get
   * @function
   * @public
   * @param key
   * @returns {Object|String|Array|Number} Item from the cache if exists, or undefined if item does not exist
   */

function get(key) {
    debug('Get', key);
    return this.store.get(key);
}

/**
   * Convenience method that enables caching/retrieving/validation of HTTP responses. Allows for custom caching
   * policy on a per-call basis. Promise is resolved with entire normalized response object including headers, body, etc.
   *
   * @name fitt.Cache#getHttp
   * @function
   * @public
   * @param {String} url URL to request. Also doubles as the caching key.
   * @param {Object} httpConfig Config object that gets passed to fitt-http module.
   * @param {String|Function|Object} cachePolicy Either a string/object representing caching policy or a function that will return a string/object representing policy. See: https://github.com/cyberthom/stale-lru-cache#setkey-value-options
   * @returns {Promise} Will get resolved with normalized response object either from cache or from HTTP operation
   */

function getHttp(url, httpConfig, cachePolicy) {

    return new Promise(function (resolve, reject) {

        // Function that gets called by stale-lru-cache to revalidate stale content
        function revalidate(url, callback) {
            var startRequest = now();

            debug('Revalidating URL', url, cachePolicy);

            http.request(url, httpConfig).
            then(function (response) {
                var curTime = now(),
                maxAge = 0,
                cachePolicyType,
                _cachePolicy;

                /*
                               * setup response meta-data
                               *
                               *  .fetched == Timestamp in ms that the response was received
                               *  .age == Number of ms that this response has been in cache
                               *  .used == Number of times the response was returned from cache
                               *  .responseTime == Amount of time it took to receive a response
                               *  .cachePolicy == The cache policy handling for the response
                               *  .expireTime == The timestamp in ms that the response should expire, if not cacheable
                               *                 defaults to the current time of the response
                               *
                               *  .fromCache == boolean true/false if the response was not in cache
                              */

                response.fetched = curTime;
                response.age = 0;
                response.used = 0;
                response.responseTime = curTime - startRequest;

                if (cachePolicy) {
                    cachePolicyType = typeof cachePolicy;
                    if (cachePolicyType === 'string' || cachePolicyType === 'object') {
                        _cachePolicy = cachePolicy;
                    } else if (cachePolicyType === 'function') {
                        _cachePolicy = cachePolicy(response);
                    }
                }
                /*
                  *
                  * Add a stricter check for `_cachePolicy`.
                  *
                  * In certain cases, the cachePolicy would default to an empty Object and a truthy check was insufficient,
                  * that would prevent the response cache headers from being honored.
                  */
                if (empty(_cachePolicy)) {
                    /**
                                           * Read the cach-control header from the response or fallback to max-age=0
                                           */
                    _cachePolicy = response.headers['cache-control'] || 'max-age=0';
                }

                response.cachePolicy = _cachePolicy;

                if (_cachePolicy) {
                    maxAge = parseMaxAge(_cachePolicy);
                }

                response.maxAge = maxAge;
                response.expireTime = curTime + maxAge * 1000;

                // Calculate response size if prop is absent
                if (!response[PROP_MEMSIZE]) {
                    response[PROP_MEMSIZE] = memSize(response);
                }

                callback(null, response, _cachePolicy);
            }).
            catch(function (error) {
                callback(error);
            });
        }
        this.store.wrap(url, revalidate, function (error, response) {
            if (error) {
                reject(error);
            } else {
                response.age = now() - response.fetched;
                if (!response.used) {
                    response.used = 1;
                    response.fromCache = false;
                } else {
                    response.used++;
                    response.fromCache = true;
                }
                debug('HTTP response retrieved', 'fromCache: ' + response.fromCache, 'timestamp: ' + response.fetched);
                resolve(response);
            }
        });
    }.bind(this));
}

/**
   * Returns a boolean indicating whether a value matching the provided key exists in the cache
   *
   * @name fitt.Cache#has
   * @function
   * @public
   * @param {String} key Caching key to look up
   * @returns {Boolean}
   */

function has(key) {
    return this.store.has(key);
}

/**
   * Returns a boolean indicating whether a particular cached value is stale based on provided rules
   *
   * @name fitt.Cache#isStale
   * @function
   * @public
   * @param key Caching key to look up
   * @returns {Boolean}
   */

function isStale(key) {
    return this.store.isStale(key);
}

/**
   * Returns all the keys stored in the cache instance
   *
   * @name fitt.Cache#keys
   * @function
   * @public
   * @returns {Array} An array of all the keys stored in the cache
   */

function keys() {
    return this.store.keys();
}

/**
   * Removes all cache entries that are past configured maxAge + staleWhileRevalidate timeframe
   * Unless explicitly forced to clear.
   *
   * @name fitt.Cache#purge
   * @function
   * @public
   *
   * @param  {Number}     maxPct      Max percentage of memory to be cleared in a single operation
   * @param  {Boolean}    force       Force clears memory while still fresh.
   * @param  {Number}     interval    Timer interval
   * @return {Object}                 Stats for the purge operation
   */

function purge(maxPct, force, interval) {
    var me = this,
    maxMemSize = me.maxMemSize,
    purgeAttempts = me.purgeAttempts,
    manifest = me.getManifest(),
    totalMemUsed = me.getMemSize(manifest),
    memExceedsLimit = totalMemUsed > maxMemSize,
    forcePurge = memExceedsLimit && me.purgeAttempts === me.maxPurgeAttemps,
    totalSuccessfulPurges,
    maxMemToClear,
    totalMemCleared,
    itemsCleared,
    clearedKeys,
    lastPurger,
    purger,
    purgerStats;

    // Try `force` purge if usage exceeds limit after max purge attempts
    if (forcePurge) {
        force = true;
        me.purgeAttempts = 0;
    }

    maxPct = getMaxPctByMemUsage(totalMemUsed, maxMemSize, MIN_PCT, maxPct);
    // percentage of total memory used that can be removed at once
    maxMemToClear = Math.round(totalMemUsed * (maxPct / ONE_HUNDRED));

    purger = getPurger(++me.totalPurgeIterations);
    purgerStats = purger.call(me, manifest, maxMemToClear, forcePurge, interval);
    totalMemCleared = purgerStats.totalMemCleared;
    itemsCleared = purgerStats.itemsCleared;
    clearedKeys = purgerStats.clearedKeys;

    // If used memory exceeds limit and we haven't cleared anything,
    // bump purge attempts
    if (memExceedsLimit && totalMemCleared === 0 && purgerStats.default) {
        me.purgeAttempts++;
    }

    if (itemsCleared) {
        me.lastPurgeAt = new Date();
        me.lastPurgeItemCount = itemsCleared;
        me.lastPurgeMemFreed = totalMemCleared;

        me.totalSuccessfulPurges++;
    }

    lastPurger = me.lastPurger =
    purgerStats.name;
    totalSuccessfulPurges = me.totalSuccessfulPurges;

    me.lastPurgeDate = new Date();
    me.currentMaxPct = maxPct;

    return {
        purgeAttempts: purgeAttempts,
        totalSuccessfulPurges: totalSuccessfulPurges,
        totalMemUsed: totalMemUsed,
        totalMemCleared: totalMemCleared,
        clearedKeys: clearedKeys,
        itemsCleared: itemsCleared,
        lastPurger: lastPurger,
        force: force };

}

/**
   * Calculate the amount of memory to purge based on mem usage.
   *
   * @param  {Number} memUsed     Total memory used
   * @param  {Number} memSize     Max memory size
   * @param  {Number} minPct      Min percentage
   * @param  {Number} maxPct      Max percentage
   * @return {Number}
   */
function getMaxPctByMemUsage(memUsed, maxMem, minPct, maxPct) {
    return Math.round(_clamp(ONE_HUNDRED * normalizeRange(memUsed, maxMem, 0), minPct, maxPct));
}

/**
   * Calculate the interval before next purge run based on mem usage.
   *
   * @param  {Number} memUsed         Total memory used
   * @param  {Number} memSize         Max memory size
   * @param  {Number} minInterval     Min interval
   * @param  {Number} maxInterval     Max interval
   * @return {Number}
   */
function getIntervalByMemUsage(memUsed, maxMem, minInterval, maxInterval) {
    return Math.round(_clamp((minInterval + (maxInterval - minInterval)) * normalizeRange(maxMem - memUsed, maxMem, 0), minInterval, maxInterval));
}

/**
   * min-max normalization
   *
   * @param  {Number} val Input
   * @param  {Number} max Maximum
   * @param  {Number} min Minimum
   * @return {Number}
   */
function normalizeRange(val, max, min) {
    var maxMinDiff = max - min;

    if (maxMinDiff === 0) {
        return 1;
    }

    return (val - min) / maxMinDiff;
}

/**
   * Get the Purger function that needs to execute,
   * based on the arguments.
   *
   * @param  {Number} totalPurgeIterations    Total purge iterations
   * @return {Function}                       Purger function to execute
   */
function getPurger(totalPurgeIterations) {
    /**
                                           * On every 3rd run, clear longlived least used items.
                                           * TODO: Factor memory available and used?
                                           */
    return totalPurgeIterations % 3 === 0 ? purgeLongLivedLeastUsed : purgeStale;
}

/**
   * Purge long lived items that are least used.
   *
   * @param  {Array} manifest         Cache manifest
   * @param  {Number} maxMemToClear   Max memory to clean
   * @param  {Boolean} force          Force clears memory while still fresh.
   * @param  {Number} interval        Timer interval
   * @return {Object}                 Purger activity stats
   */
function purgeLongLivedLeastUsed(manifest, maxMemToClear, force, interval) {
    var me = this,
    orderedManifest = _orderBy(manifest,
    [
    PROP_MAXAGE,
    PROP_USED],

    [
    SORT_DESC,
    SORT_ASC]),


    clearedKeys = [],
    cnt = orderedManifest && orderedManifest.length,
    totalMemCleared = 0,
    idx = 0,
    itemsCleared = 0,
    cacheEntry,
    key,
    used,
    size,
    age;

    for (; idx < cnt; idx++) {
        cacheEntry = orderedManifest[idx];
        key = cacheEntry && cacheEntry.key;
        used = cacheEntry && cacheEntry.used;
        age = cacheEntry && cacheEntry.age;
        size = cacheEntry && cacheEntry.size;

        if (totalMemCleared < maxMemToClear) {
            if (used <= 1 && age >= interval) {
                me.delete(key);
                clearedKeys.push(key);
                itemsCleared++;
                totalMemCleared += size;
                debug('deleted', key, size, totalMemCleared, force);
            }
        } else {
            break;
        }

    }

    return {
        name: 'purgeLongLivedLeastUsed',
        totalMemCleared: totalMemCleared,
        clearedKeys: clearedKeys,
        itemsCleared: itemsCleared };


}

/**
   * Stale items purger
   * (default)
   *
   * @param  {Array} manifest         Cache manifest
   * @param  {Number} maxMemToClear   Max memory to clean
   * @param  {Boolean} force          Force clears memory while still fresh.
   * @return {Object}                 Purger activity stats
   */
function purgeStale(manifest, maxMemToClear, force) {
    var me = this,
    orderedManifest = orderByPurgingPriority(manifest, force),
    cnt = orderedManifest && orderedManifest.length,
    clearedKeys = [],
    totalMemCleared = 0,
    idx = 0,
    itemsCleared = 0,
    cacheEntry,
    key,
    size;

    for (; idx < cnt; idx++) {
        cacheEntry = orderedManifest[idx];
        key = cacheEntry && cacheEntry.key;
        size = cacheEntry && cacheEntry.size;

        if (totalMemCleared < maxMemToClear) {
            // (has == false) - technically the cache still has the item,
            // but it has expired and is past revalidation timeframe
            if (!me.has(key) || force) {
                clearedKeys.push(key);
                me.delete(key);
                itemsCleared++;
                totalMemCleared += size;
                debug('deleted', key, size, totalMemCleared, force);
            }
        } else {
            break;
        }
    }

    return {
        name: 'purgeStale',
        default: true,
        clearedKeys: clearedKeys,
        totalMemCleared: totalMemCleared,
        itemsCleared: itemsCleared };

}

/**
   * Enables purge timer
   *
   * @name fitt.Cache#enablePurger
   * @function
   * @public
   *
   * @param  {Number} interval    Polling interval
   * @param  {Function} cb        Callback
   * @return {undefined}
   */
function enablePurger(interval, cb) {
    var me = this,
    maxInterval = interval || me.maxPurgeInterval,
    maxPct = me.maxPurgePct,
    nextInterval,
    memUsed,
    stats;

    me.timer = setTimeout(function cachePurgeTimer() {
        stats = me.purge(maxPct, null, maxInterval);
        memUsed = stats.totalMemUsed - stats.totalMemCleared;
        nextInterval = getIntervalByMemUsage(memUsed, me.maxMemSize, me.minPurgeInterval, me.maxPurgeInterval);

        me.currentInterval = nextInterval;

        if (fitt.isFunc(cb)) {
            cb(stats, nextInterval);
        }

        if (me.timer) {
            me.enablePurger(nextInterval, cb);
        }
    }, maxInterval);
}

/**
   * Clears the purge timer
   *
   * @name fitt.Cache#disablePurger
   * @function
   * @public
   *
   * @return {undefined}
   */
function disablePurger() {
    var me = this;

    clearTimeout(me.timer);
    me.timer = null;
}

/**
   * Get `memUsed`, actual memory size of LRU cache
   *
   * @name fitt.Cache#getMemSize
   * @function
   * @public
   *
   * @param {Array} manifest  Cache manifest
   * @return {Number}         Memory size in bytes
   */

function getMemSize(manifest) {
    var me = this,
    totalSize = 0;

    manifest = manifest || me.getManifest();

    fitt.forEach(manifest, function (val) {
        totalSize += val.size;
    });

    return totalSize;
}

/**
   * Builds a cache manifest file, based on the stored values
   *
   * @name fitt.Cache#getManifest
   * @function
   * @public
   *
   * @return {Array}      Cache manifest
   */

function getManifest() {
    var me = this,
    // LRU's internal cache Map
    map = me && me.store && me.store._map,
    cacheManifest = [],
    curTime = now();

    if (map instanceof Map) {
        map.forEach(function (v, key) {
            var src = v && v.value && v.value.value,
            maxAge,size,used,responseTime,
            fromCache,fetched,age,expireTime,cachePolicy,memSize;

            if (src) {
                maxAge = src.maxAge;
                size = src[PROP_MEMSIZE];
                used = src.used;
                responseTime = src.responseTime;
                fromCache = src.fromCache;
                fetched = src.fetched;
                expireTime = src.expireTime;
                cachePolicy = src.cachePolicy;
                age = src.age;
                memSize = src.__memsize;

                if (fetched > 0 && fetched <= curTime) {
                    src.age = curTime - fetched;
                }

                // Normalize use count
                if (!used || used < 0) {
                    src.used = 1;
                }

                cacheManifest.push(
                new CacheManifest(key, maxAge, size, used, responseTime, fromCache, fetched, age, expireTime, cachePolicy, memSize));

            }
        });
    }

    return cacheManifest;
}

/**
   * CacheManifest
   *
   * @param {String} key          Cache Key
   * @param {Number} maxAge       Max Age for the corresponding key
   * @param {Number} size         Memory used
   * @param {Number} used         Number of times, resource was used from Cache
   * @param {Number} responseTime Time taken for receive API response
   * @param {Boolean} fromCache   Indicates if response if from cache
   * @param {Number} fetched      Timestamp in ms that the response was received
   * @param {Number} age          Number of ms that this response has been in cache
   * @param {Number} expireTime   The date/time after which the response is considered stale
   * @param {Number} cachePolicy  Cache policy used for requesting the resource
   * @param {Number} memSize      Size in memory
   */

function CacheManifest(key, maxAge, size, used, responseTime, fromCache, fetched, age, expireTime, cachePolicy, memSize) {

    var me = this;

    me.key = key;
    me.maxAge = maxAge;
    me.size = size;
    me.used = used;
    me.responseTime = responseTime;
    me.fromCache = fromCache;
    me.fetched = fetched;
    me.expireTime = expireTime;
    me.cachePolicy = cachePolicy;
    me.age = age;
    me.memSize = memSize;
}


/**
   * Orders the Cache Manifest, based on purging priority
   *
   * @param  {Array}      cacheManifest   Cache Manifest
   * @param  {Boolean}    forcePurge      Is being force purged
   * @return {Array}                      Ordered cacheManifest
   */

function orderByPurgingPriority(cacheManifest, forcePurge) {
    var sortProps = [
    PROP_SIZE,
    PROP_MAXAGE,
    PROP_USED],

    sortOrder = [
    SORT_DESC,
    SORT_DESC,
    SORT_ASC];


    // As usage exceeds limit, we may often end up force clearing,
    // Change the sort order, to prioritize based on least used > maxAge > size
    // TODO: revisit and tweak as required
    if (forcePurge) {
        sortProps = [
        PROP_USED,
        PROP_MAXAGE,
        PROP_SIZE];


        sortOrder = [
        SORT_ASC,
        SORT_DESC,
        SORT_DESC];

    }

    return _orderBy(
    cacheManifest,
    sortProps,
    sortOrder);

}

/**
   * Removes all items from the cache
   *
   * @name fitt.Cache#reset
   * @function
   * @public
   */

function reset() {
    this.store.reset();
}

/**
   * Sets a key/value pair in the cache, applying the supplied caching policy or the default
   *
   * @name fitt.Cache#set
   * @function
   * @public
   * @param {String} key
   * @param {Object|String|Array|Number} value Value to be stored in the cache
   * @param {Object|String} options Either an object or string representing caching policy. See: https://github.com/cyberthom/stale-lru-cache#setkey-value-options
   */

function set(key, value, options) {
    var size = value && value[PROP_MEMSIZE];

    if (value && !size) {
        size = memSize(value);
        value[PROP_MEMSIZE] = size;
    }

    debug('Set', key, value, options);
    return this.store.set(key, value, options);
}

/**
   * Returns the size of all the stored items in the cache
   *
   * @name fitt.Cache#size
   * @function
   * @public
   * @returns {Number} Size of all the stored items
   */

function size() {
    return this.store.size;
}

Cache[PROTOTYPE] = {
    getMemSize: getMemSize,
    getManifest: getManifest,
    delete: deleteItem,
    get: get,
    getHttp: getHttp,
    has: has,
    isStale: isStale,
    keys: keys,
    purge: purge,
    enablePurger: enablePurger,
    disablePurger: disablePurger,
    reset: reset,
    set: set,
    size: size };


module.exports = Cache;


//////////////////
// WEBPACK FOOTER
// ../~/espn-fitt/es5/fitt-cache/index.js
// module id = 825
// module chunks = 0